/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable import/no-extraneous-dependencies */
import { Injectable } from '@angular/core';
import { interval, of } from 'rxjs';
import { MessageService } from '@arc/aureus';
import {
  catchError,
  concatMap,
  exhaustMap,
  filter,
  map,
  mergeMap,
  take,
  takeWhile,
} from 'rxjs/operators';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
  AppConfigSettings,
  UserNotificationAction,
} from '@arc/arc-shared-frontend';
import { TranslateService } from '@ngx-translate/core';
import { HttpErrorResponse } from '@angular/common/http';
import {
  GetSessionSummaryResponse,
  LoadingSpinnerService,
  PaymentInformationPreviewHelperService,
  PaymentInformationServiceFactory,
  PaymentPreviewErrorPayload,
  PaymentPreviewHelperService,
  PaymentPreviewV1Response,
  PaymentSourceType,
  PaymentStatus,
  PayOtherAmountV1ApiRequest,
  PayOtherAmountV1Error,
  PayOtherAmountV1Response,
  PayOtherAmountV1Status,
  TransactionAuthorizationResult,
} from '@arc/unified-payments-frontend/shared-payment';
import {
  BtPayPaymentInformationActionsUnion,
  CreatePaymentPreviewAllPayablesPaidInFullAction,
  CreatePaymentPreviewCompleteAction,
  CreatePaymentPreviewErrorAction,
  UpdatePaymentPreviewCompleteAction,
  UpdatePaymentPreviewErrorAction,
  UpdatePayOtherAmountErrorAction,
} from '../actions/btpay-payment-information.actions';
import { BtPayPaymentInformationActions, PayActions } from '../actions';
import {
  selectPaymentAuthorizationPending,
  selectPaymentDate,
  selectPaymentPreviewTotalAmount,
  selectPaymentReferenceBurn,
  selectPaymentReferenceNumber,
  selectPaymentStatusCheckCount,
  selectSessionPaymentAccount,
  selectSingleTransactionReferenceNumber,
  selectTotalSurcharge,
} from '../selectors/btpay.selectors';
import { SessionStore } from '../../session-store/reducers/session.reducer';
import {
  selectCurrencyCode,
  selectTotalAmount,
  selectVirtualOtherwisePrimaryTenantId,
} from '../../session-store/selectors/session.selectors';
import {
  selectSortedSessionDocuments,
  SessionActions,
} from '../../session-store';
import {
  selectPaymentExclusionOverrideSetting,
  selectPaymentPreviewDocuments,
} from '../../shared-selectors';
import { concatLatestFrom } from '@ngrx/operators';
import { PoiIntlMakePaymentComponentActions } from '../../component-actions';
import { selectAllowStatementPaymentsRule } from '../../payment-rules';
import { ht } from 'date-fns/locale';

@Injectable()
export class BtPayPaymentInformationEffects {
  private checkPaymentStatusTimeout = +this.appConfig.getconfig(
    'checkPaymentStatusTimeout',
  );

  private checkPaymentStatusInterval = +this.appConfig.getconfig(
    'checkPaymentStatusInterval',
  );

  constructor(
    private actions$: Actions<BtPayPaymentInformationActionsUnion>,
    private paymentInformationServiceFactory: PaymentInformationServiceFactory,
    private translationService: TranslateService,
    private loadingSpinnerService: LoadingSpinnerService,
    private store: Store,
    private messageService: MessageService,
    private previewHelperService: PaymentInformationPreviewHelperService,
    private appConfig: AppConfigSettings,
  ) {}

  lastError: PaymentPreviewErrorPayload = null;

  createPaymentPreview$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BtPayPaymentInformationActions.CreatePaymentPreviewAction.type),
      concatLatestFrom(() => [
        this.store.select(selectPaymentReferenceBurn),
        this.store.select(SessionStore),
        this.store.select(selectTotalAmount),
        this.store.select(selectSortedSessionDocuments),
        this.store.select(selectCurrencyCode),
        this.store.select(selectPaymentDate),
        this.store.select(selectSessionPaymentAccount),
        this.store.select(selectPaymentExclusionOverrideSetting),
        this.store.select(selectVirtualOtherwisePrimaryTenantId),
        this.store.select(selectAllowStatementPaymentsRule),
      ]),
      exhaustMap(
        ([
          ,
          referenceNumber,
          sessionSummary,
          totalAmount,
          selectedDocuments,
          currencyCode,
          paymentDate,
          paymentAccount,
          paymentAllowOverrideCC,
          tenantId,
          allowStatementPayments,
        ]) => {
          const paymentPreviewV1Request =
            PaymentPreviewHelperService.buildPaymentPreviewRequest(
              referenceNumber,
              sessionSummary,
              totalAmount,
              0, // The Total Surcharge passed in the initial create request has to be <= 0
              selectedDocuments,
              currencyCode,
              paymentDate,
              paymentAccount,
              paymentAllowOverrideCC,
              allowStatementPayments,
            );
          return this.paymentInformationServiceFactory
            .create()
            .updatePaymentPreviewV1(tenantId, paymentPreviewV1Request)
            .pipe(
              map((paymentPreview: PaymentPreviewV1Response) => {
                if (sessionSummary.payment) {
                  this.previewHelperService.syncSummaryPreviewWithPayment(
                    paymentPreview,
                    sessionSummary.payment,
                  );
                }
                return CreatePaymentPreviewCompleteAction({
                  payload: paymentPreview,
                });
              }),
              catchError((error: HttpErrorResponse) => {
                const isAllPayablesPaidInFull =
                  error.status === 400 &&
                  Array.isArray(error.error?.errors) &&
                  error.error.errors.some(
                    (e: { code: string }) =>
                      e.code === 'all.payables.paid.in.full',
                  );

                if (isAllPayablesPaidInFull) {
                  return of(
                    CreatePaymentPreviewAllPayablesPaidInFullAction({
                      payload: error,
                    }),
                  );
                }

                return of(
                  CreatePaymentPreviewErrorAction({
                    emptyPayables:
                      paymentPreviewV1Request.payment.payables.length == 0,
                  }),
                );
              }),
            );
        },
      ),
    ),
  );

  createPaymentPreviewError$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        BtPayPaymentInformationActions.CreatePaymentPreviewErrorAction.type,
      ),
      map((action) => {
        return new UserNotificationAction({
          type: 'error',
          message: action.emptyPayables
            ? (this.translationService.instant(
                'ARC_PAYMENT_SESSION_ONLY_CONTAINS_UNPAYABLE_DOCUMENT_TYPES',
              ) as string)
            : (this.translationService.instant(
                'ARC_PAYMENT_INTL_GENERIC_ERROR',
              ) as string),
        });
      }),
    ),
  );

  createPaymentPreviewAllPayablesPaidInFullError$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CreatePaymentPreviewAllPayablesPaidInFullAction),
      map((action) => {
        const message: string = `${
          this.translationService.instant(
            'ARC_PAYMENT_NO_PAYABLE_DOCUMENTS',
          ) as string
        } ${action.payload.error?.errors?.[0]?.message}`;

        return new UserNotificationAction({
          type: 'error',
          message,
        });
      }),
    ),
  );

  updatePaymentPreview$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        BtPayPaymentInformationActions.UpdatePaymentPreviewAction.type,
        BtPayPaymentInformationActions.UpdatePaymentReferenceAction.type,
        PayActions.SetPaymentAccountAction.type,
        PoiIntlMakePaymentComponentActions.buyerAccountSelected,
        SessionActions.applySoftBlockDocumentDeselection,
      ),
      concatLatestFrom(() => [
        this.store.select(selectVirtualOtherwisePrimaryTenantId),
        this.store.select(selectPaymentReferenceBurn),
        this.store.select(SessionStore),
        this.store.select(selectPaymentPreviewTotalAmount),
        this.store.select(selectTotalSurcharge),
        this.store.select(selectPaymentPreviewDocuments),
        this.store.select(selectCurrencyCode),
        this.store.select(selectPaymentDate),
        this.store.select(selectSessionPaymentAccount),
        this.store.select(selectPaymentExclusionOverrideSetting),
        this.store.select(selectAllowStatementPaymentsRule),
      ]),
      filter(([, , , , , , selectedDocuments]) => selectedDocuments.length > 0),
      mergeMap(
        ([
          ,
          tenantId,
          referenceNumber,
          sessionSummary,
          totalAmount,
          totalSurcharge,
          selectedDocuments,
          currencyCode,
          paymentDate,
          paymentAccount,
          paymentAllowOverrideCC,
          allowStatementPayments,
        ]) => {
          const paymentPreviewV1Request =
            PaymentPreviewHelperService.buildPaymentPreviewRequest(
              referenceNumber,
              sessionSummary,
              totalAmount,
              totalSurcharge,
              selectedDocuments,
              currencyCode,
              paymentDate,
              paymentAccount,
              paymentAllowOverrideCC,
              allowStatementPayments,
            );
          return this.paymentInformationServiceFactory
            .create()
            .updatePaymentPreviewV1(tenantId, paymentPreviewV1Request)
            .pipe(
              map((paymentPreview: PaymentPreviewV1Response) => {
                return UpdatePaymentPreviewCompleteAction({
                  payload: paymentPreview,
                });
              }),
              catchError((error: HttpErrorResponse) => {
                return of(
                  UpdatePaymentPreviewErrorAction({
                    payload: error,
                  }),
                );
              }),
            );
        },
      ),
    ),
  );

  updatePayOtherAmount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BtPayPaymentInformationActions.UpdatePayOtherAmountAction),
      concatLatestFrom(() => [
        this.store.select(SessionStore),
        this.store.select(selectVirtualOtherwisePrimaryTenantId),
      ]),
      mergeMap(([, sessionSummary, tenantId]) => {
        const request = this.buildPayOtherRequest(sessionSummary);
        return this.paymentInformationServiceFactory
          .create()
          .GetPayOtherDocuments(tenantId, request)
          .pipe(
            concatMap((payOtherResponse: PayOtherAmountV1Response) => [
              SessionActions.UpdatePayOtherPayablesAction({
                payload: payOtherResponse,
              }),
              BtPayPaymentInformationActions.UpdatePayOtherAmountCompleteAction(
                {
                  payload: payOtherResponse,
                },
              ),
            ]),
            catchError((error: HttpErrorResponse) => {
              return of(
                UpdatePayOtherAmountErrorAction({
                  payload: error,
                }),
              );
            }),
          );
      }),
    ),
  );

  updatePayOtherAmountStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        BtPayPaymentInformationActions.UpdatePayOtherAmountCompleteAction.type,
      ),
      map(
        (payOtherAmount) =>
          payOtherAmount?.payload?.payOtherReasonCodes?.filter(
            (reasonCode) =>
              reasonCode == PayOtherAmountV1Status.AllDocumentsPaid ||
              reasonCode == PayOtherAmountV1Status.PartialManagedAutoPay ||
              reasonCode == PayOtherAmountV1Status.UnableToPayOtherAmount,
          ),
      ),
      filter((payOtherReasonCodes) => !!payOtherReasonCodes?.length),
      mergeMap((payOtherReasonCodes) =>
        payOtherReasonCodes.map((statusString) => {
          const statusKeySuffix: string = statusString
            .toUpperCase()
            .replace(/-/g, '_');

          return new UserNotificationAction({
            type: 'info',
            message: this.translationService.instant(
              `ARC_PAY_OTHER_${statusKeySuffix}`,
            ) as string,
            sticky: true,
          });
        }),
      ),
    ),
  );

  updatePayOtherAmountError$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        BtPayPaymentInformationActions.UpdatePayOtherAmountErrorAction.type,
      ),
      filter(
        (error) =>
          (error as any)?.payload?.error?.errors?.some(
            (e) => e.code === PayOtherAmountV1Error.DocumentNotFoundForAccounts,
          ),
      ),
      map(() => {
        return new UserNotificationAction({
          type: 'error',
          message: this.translationService.instant(
            'ARC_PAYMENT_OTHER_NO_ELIGIBLE_OPEN_DOCUMENTS_ERROR',
          ) as string,
        });
      }),
    ),
  );

  submitPaymentPreview$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BtPayPaymentInformationActions.SubmitPaymentPreviewAction.type),
      concatLatestFrom(() => [
        this.store.select(selectPaymentReferenceBurn),
        this.store.select(SessionStore),
        this.store.select(selectPaymentPreviewTotalAmount),
        this.store.select(selectTotalSurcharge),
        this.store.select(selectPaymentPreviewDocuments),
        this.store.select(selectCurrencyCode),
        this.store.select(selectPaymentDate),
        this.store.select(selectSessionPaymentAccount),
        this.store.select(selectPaymentExclusionOverrideSetting),
        this.store.select(selectVirtualOtherwisePrimaryTenantId),
        this.store.select(selectAllowStatementPaymentsRule),
      ]),
      exhaustMap(
        ([
          action,
          referenceNumber,
          sessionSummary,
          totalAmount,
          totalSurcharge,
          selectedDocuments,
          currencyCode,
          paymentDate,
          paymentAccount,
          paymentAllowOverrideCC,
          tenantId,
          allowStatementPayments,
        ]) => {
          const paymentPreviewV1Request =
            PaymentPreviewHelperService.buildPaymentPreviewRequest(
              referenceNumber,
              sessionSummary,
              totalAmount,
              totalSurcharge,
              selectedDocuments,
              currencyCode,
              paymentDate,
              paymentAccount,
              paymentAllowOverrideCC,
              allowStatementPayments,
              action.paymentProcess,
            );

          const nonZeroAmountPayables =
            paymentPreviewV1Request.payment.payables.filter(
              (payable) => payable.amount !== 0,
            );
          paymentPreviewV1Request.payment.payables = nonZeroAmountPayables;

          return this.paymentInformationServiceFactory
            .create()
            .updatePaymentPreviewV1(tenantId, paymentPreviewV1Request)
            .pipe(
              map((paymentPreview: PaymentPreviewV1Response) => {
                return BtPayPaymentInformationActions.SubmitPaymentPreviewCompleteAction(
                  {
                    payload: paymentPreview,
                    paymentProcess: action.paymentProcess,
                  },
                );
              }),
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              catchError((error: HttpErrorResponse) => {
                return of(
                  BtPayPaymentInformationActions.SubmitPaymentPreviewErrorAction(
                    {
                      payload: error,
                    },
                  ),
                );
              }),
            );
        },
      ),
    ),
  );

  submitPaymentPreviewError$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        BtPayPaymentInformationActions.SubmitPaymentPreviewErrorAction.type,
      ),
      map((x) => x.payload),
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      map((error: HttpErrorResponse) => {
        return new UserNotificationAction({
          type: 'error',
          message: this.translationService.instant(
            'ARC_PAYMENT_INTL_GENERIC_ERROR',
          ) as string,
        });
      }),
    ),
  );

  /* istanbul ignore next */
  loadUntilPaymentPreviewComplete$ = createEffect(
    () =>
      this.loadUntilActionsComplete(
        [
          BtPayPaymentInformationActions.CreatePaymentPreviewAction.type,
          BtPayPaymentInformationActions.UpdatePaymentPreviewAction.type,
          BtPayPaymentInformationActions.UpdatePaymentReferenceAction.type,
          PayActions.SetPaymentAccountAction.type,
          BtPayPaymentInformationActions.UpdatePayOtherAmountAction.type,
        ],
        [
          BtPayPaymentInformationActions.CreatePaymentPreviewCompleteAction
            .type,
          BtPayPaymentInformationActions.CreatePaymentPreviewErrorAction.type,
          CreatePaymentPreviewAllPayablesPaidInFullAction.type,
          BtPayPaymentInformationActions.UpdatePaymentPreviewCompleteAction
            .type,
          BtPayPaymentInformationActions.UpdatePaymentPreviewErrorAction.type,
          BtPayPaymentInformationActions.UpdatePayOtherAmountErrorAction.type,
        ],
      ),
    { dispatch: false },
  );

  executeSingleTransactionPaymentPreview$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        BtPayPaymentInformationActions
          .ExecuteSingleTransactionPaymentPreviewAction.type,
      ),
      concatLatestFrom(() => [
        this.store.select(selectVirtualOtherwisePrimaryTenantId),
        this.store.select(selectPaymentReferenceNumber),
        this.store.select(selectSingleTransactionReferenceNumber),
      ]),
      exhaustMap(([, tenantId, paymentReference, transactionReference]) =>
        this.paymentInformationServiceFactory
          .create()
          .executePaymentV1(
            tenantId,
            paymentReference,
            transactionReference,
            null,
          )
          .pipe(
            map((response) => {
              const result: TransactionAuthorizationResult = {
                paymentDate: response.paymentDate,
                gpalStatus: response.status as PaymentStatus,
              };

              return BtPayPaymentInformationActions.ExecutePaymentPreviewSuccessAction(
                { result, transactionReference },
              );
            }),
            catchError((error: HttpErrorResponse) =>
              of(
                error.status >= 500
                  ? BtPayPaymentInformationActions.ExecutePaymentPreviewServerErrorAction(
                      { payload: error },
                    )
                  : BtPayPaymentInformationActions.ExecutePaymentPreviewBadRequestErrorAction(
                      { payload: error },
                    ),
              ),
            ),
          ),
      ),
    ),
  );

  displayExecutePaymentPreviewError$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        BtPayPaymentInformationActions.ExecutePaymentPreviewBadRequestErrorAction,
      ),
      concatLatestFrom(() => [
        this.store.select(selectVirtualOtherwisePrimaryTenantId),
      ]),
      exhaustMap(() =>
        of(
          new UserNotificationAction({
            type: 'error',
            message: this.translationService.instant(
              'ARC_PAYMENT_UNEXPECTED_ERROR',
            ) as string,
          }),
        ),
      ),
    ),
  );

  resetPaymentRefernceOnExecutePaymentError$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        BtPayPaymentInformationActions.ExecutePaymentPreviewBadRequestErrorAction,
      ),
      exhaustMap(() =>
        of(BtPayPaymentInformationActions.UpdatePaymentReferenceAction()),
      ),
    ),
  );

  /* istanbul ignore next */
  pollPaymentStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        BtPayPaymentInformationActions.ExecutePaymentPreviewServerErrorAction,
      ),
      concatLatestFrom(() => [
        this.store.select(selectVirtualOtherwisePrimaryTenantId),
        this.store.select(selectPaymentReferenceNumber),
      ]),
      exhaustMap(([, tenantId, paymentReferenceNumber]) =>
        interval(this.checkPaymentStatusInterval).pipe(
          concatLatestFrom(() => [
            this.store.select(selectPaymentAuthorizationPending),
            this.store.select(selectPaymentStatusCheckCount),
          ]),
          takeWhile(
            ([, authorizationPending, statusCheckCount]) =>
              authorizationPending &&
              statusCheckCount < this.checkPaymentStatusTimeout,
          ),
          exhaustMap(() =>
            of(
              PayActions.CheckPaymentStatusAction({
                payload: {
                  masterConfirmationNumber: paymentReferenceNumber,
                  tenantId,
                },
              }),
            ),
          ),
        ),
      ),
    ),
  );

  getBuyerAccounts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BtPayPaymentInformationActions.GetBuyerAccountsAction),
      concatLatestFrom(() => [
        this.store.select(selectVirtualOtherwisePrimaryTenantId),
        this.store.select(SessionStore),
      ]),
      exhaustMap(([, tenantId, { externalUserUrn, originatingApplication }]) =>
        this.paymentInformationServiceFactory
          .create()
          .getBuyerAccountsV1(tenantId, externalUserUrn, originatingApplication)
          .pipe(
            map((response) =>
              BtPayPaymentInformationActions.GetBuyerAccountsSuccessAction({
                buyerAccounts: response.buyerAccounts,
              }),
            ),
            catchError(() =>
              of(BtPayPaymentInformationActions.GetBuyerAccountsErrorAction()),
            ),
          ),
      ),
    ),
  );

  /* istanbul ignore next */
  loadUntilBuyerAccountsComplete$ = createEffect(
    () =>
      this.loadUntilActionsComplete(
        [BtPayPaymentInformationActions.GetBuyerAccountsAction.type],
        [
          BtPayPaymentInformationActions.GetBuyerAccountsSuccessAction.type,
          BtPayPaymentInformationActions.GetBuyerAccountsErrorAction.type,
        ],
      ),
    { dispatch: false },
  );

  // eslint-disable-next-line class-methods-use-this
  private buildPayOtherRequest(sessionSummary: GetSessionSummaryResponse) {
    const payOtherAmountV1ApiRequest: PayOtherAmountV1ApiRequest = {
      payOtherAmount: sessionSummary.amountToPay,
      buyerAccount:
        sessionSummary.buyerAccountIdentifier?.replace('accountNumber:', '') ??
        '',
      paymentInitiator: {
        paymentSourceType: PaymentSourceType.PayOtherAmount,
        paymentSource: sessionSummary.originatingApplication,
        embeddedPaymentInitiationSource:
          sessionSummary.embeddedPaymentInitiationSource,
      },
      externalUser: {
        burn: sessionSummary.externalUserUrn
          ? sessionSummary.externalUserUrn
          : null,
      },
    };
    return payOtherAmountV1ApiRequest;
  }

  /* istanbul ignore next */
  private loadUntilActionsComplete(
    sourceActionTypes: string[],
    completeActionTypes: string[],
  ) {
    return this.actions$.pipe(
      ofType(...sourceActionTypes),
      exhaustMap(() => {
        const completeActions$ = this.actions$.pipe(
          ofType(...completeActionTypes),
          take(1),
        );

        this.loadingSpinnerService.loadUntil(completeActions$);

        return of();
      }),
    );
  }
}
